import React, { useEffect, useRef, useState } from "react";


let push = null; // set by <DialogHost />
const queue = [];

const send = (item) => {
  if (push) push(item);
  else queue.push(item);
};

export function confirmDialog(message, opts = {}) {
  return new Promise((resolve) => send({ kind: "confirm", message: String(message ?? ""), resolve, ...opts }));
}

export function alertDialog(message, opts = {}) {
  return new Promise((resolve) => send({ kind: "alert", message: String(message ?? ""), resolve, ...opts }));
}

const guessTone = (text) => {
  const t = text.toLowerCase();
  if (/(success|successfully|saved|created|updated|deleted|changed)/.test(t) && !/(fail|error|not )/.test(t)) return "success";
  if (/(fail|error|unable|invalid|cannot|can't|denied|not allowed|do not match|must not|unsupported)/.test(t)) return "error";
  if (/(please|enter|select|required|make sure|must)/.test(t)) return "warning";
  return "info";
};

let lastAlert = { text: "", at: 0 };

export function installDialogs(toast) {
  window.alert = (message) => {
    const text = String(message ?? "");
    // a page that reports the same failure a moment after the automatic message would stack two dialogs: show one
    if (Date.now() - lastAlert.at < 1200 && !/success|saved|created|updated|deleted/i.test(text)) return;
    lastAlert = { text, at: Date.now() };
    const tone = guessTone(text);
    if (tone === "success" && toast) {
      toast.success(text);
      return;
    }
    alertDialog(text, { tone });
  };
}

const TONES = {
  success: { color: "#059669", bg: "#ECFDF5", title: "Success", icon: "M20 6 9 17l-5-5" },
  error: { color: "#DC2626", bg: "#FEF2F2", title: "Something went wrong", icon: "M18 6 6 18M6 6l12 12" },
  warning: { color: "#D97706", bg: "#FFFBEB", title: "Please check", icon: "M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" },
  info: { color: "#2E86AB", bg: "#EBF8FF", title: "Notice", icon: "M12 16v-4M12 8h.01M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0z" },
  danger: { color: "#DC2626", bg: "#FEF2F2", title: "Are you sure?", icon: "M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" },
  confirm: { color: "#2E86AB", bg: "#EBF8FF", title: "Please confirm", icon: "M9 9a3 3 0 1 1 4.2 2.7c-.8.4-1.2 1-1.2 1.8M12 17h.01M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0z" },
};

function Icon({ path, color }) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

export function DialogHost() {
  const [current, setCurrent] = useState(null);
  const pending = useRef([]);
  const okRef = useRef(null);

  useEffect(() => {
    const show = (item) => setCurrent((cur) => {
      if (cur) { pending.current.push(item); return cur; }
      return item;
    });
    push = show;
    queue.splice(0).forEach(show);
    return () => { push = null; };
  }, []);

  const close = (result) => {
    if (!current) return;
    current.resolve(result);
    const next = pending.current.shift() || null;
    setCurrent(next);
  };

  useEffect(() => {
    if (!current) return undefined;
    const prev = document.activeElement;
    okRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(false); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prev && prev.focus) prev.focus();
    };
    // eslint-disable-next-line
  }, [current]);

  if (!current) return null;
  const isConfirm = current.kind === "confirm";
  const danger = isConfirm && (current.tone === "danger" || (!current.tone && /\b(delete|remove)\b/i.test(current.message)));
  const tone = current.tone && TONES[current.tone] ? current.tone : isConfirm ? (danger ? "danger" : "confirm") : "info";
  const t = TONES[tone];
  const title = current.title || (isConfirm && danger ? "Delete this?" : t.title);
  const okText = current.confirmText || (isConfirm ? (danger ? "Delete" : "Confirm") : "OK");

  return (
    <div className="dlg-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && isConfirm) close(false); }}>
      <div className="dlg-card" role="alertdialog" aria-modal="true" aria-labelledby="dlg-title" aria-describedby="dlg-msg">
        <div className="dlg-icon" style={{ background: t.bg }}><Icon path={t.icon} color={t.color} /></div>
        <h2 id="dlg-title" className="dlg-title">{title}</h2>
        <p id="dlg-msg" className="dlg-msg">{current.message}</p>
        <div className="dlg-actions">
          {isConfirm && (
            <button type="button" className="dlg-btn dlg-btn-ghost" onClick={() => close(false)}>
              {current.cancelText || "Cancel"}
            </button>
          )}
          <button
            type="button"
            ref={okRef}
            className="dlg-btn dlg-btn-primary"
            style={{ background: t.color }}
            onClick={() => close(true)}
          >
            {okText}
          </button>
        </div>
      </div>
    </div>
  );
}
